/**
 * DOM helpers for the compose editor (spec §11): selection memory, `execCommand` wrappers with
 * Range fallbacks, inline (cid) image insertion, paste/drop extraction, the attachment-mention
 * check over the live editor and the mobile keyboard lift. Everything here touches the DOM; the
 * pure string/FormData logic stays in `$lib/mail/compose`.
 */
import { mentionsAttachment } from '$lib/mail/compose';
import { isMobile } from '$lib/util/dom';

/** Toolbar commands passed straight to `execCommand`. */
export type EditorCommand = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'removeFormat';

/** The remembered editor selection (`null` until the caret has been inside the editor). */
export type SavedRange = Range | null;

/** Files chosen in a file input, read from a `change` event. */
export function fileInputFiles(ev: Event): File[] {
  const input = ev.currentTarget as HTMLInputElement | null;
  return [...(input?.files ?? [])];
}

/** Reset a file input so the same file can be chosen again. */
export function clearFileInput(ev: Event): void {
  const input = ev.currentTarget as HTMLInputElement | null;
  if (input) input.value = '';
}

/** `innerText` of the editor; jsdom has no `innerText`, so `textContent` stands in. */
export function editorText(el: HTMLElement | null | undefined): string {
  if (!el) return '';
  const text = (el as { innerText?: string }).innerText;
  return text ?? el.textContent ?? '';
}

/** Clone of the current selection range when it lies inside `editor`, else null. */
export function selectionIn(editor: HTMLElement): Range | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  return editor.contains(range.commonAncestorContainer) ? range.cloneRange() : null;
}

/**
 * Legacy `dt` prologue: focus the editor and put the remembered range back when the live
 * selection is empty or collapsed (a toolbar click has just moved focus out of the editor).
 */
export function restoreSelection(editor: HTMLElement, saved: Range | null): void {
  editor.focus();
  const sel = window.getSelection();
  if (saved && sel && (!sel.rangeCount || sel.isCollapsed)) {
    sel.removeAllRanges();
    sel.addRange(saved);
  }
}

type ExecDocument = Document & { execCommand?: (command: string, showUI: boolean, value?: string) => boolean };

/** `document.execCommand` guarded for environments without it (jsdom); false when unavailable. */
export function execCommand(command: string, value?: string): boolean {
  const doc = document as ExecDocument;
  if (typeof doc.execCommand !== 'function') return false;
  try {
    return doc.execCommand(command, false, value);
  } catch {
    return false;
  }
}

function liveRangeIn(editor: HTMLElement): { sel: Selection; range: Range } | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  return editor.contains(range.commonAncestorContainer) ? { sel, range } : null;
}

/**
 * `insertHTML` at the caret, falling back to a Range insertion when the command is refused or
 * missing. Without a caret inside the editor the html is appended.
 */
export function insertHtml(editor: HTMLElement, html: string): void {
  if (execCommand('insertHTML', html)) return;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const live = liveRangeIn(editor);
  if (!live) {
    editor.appendChild(tpl.content);
    return;
  }
  const { sel, range } = live;
  const last = tpl.content.lastChild;
  range.deleteContents();
  range.insertNode(tpl.content);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** `insertImage` (data URL) with the same fallback as `insertHtml`. */
export function insertImage(editor: HTMLElement, src: string): void {
  if (execCommand('insertImage', src)) return;
  insertHtml(editor, `<img src="${escapeAttr(src)}">`);
}

/** Placeholder for a pasted/dropped image: `src` is a blob URL, `data-tik-cid` maps to the file. */
export function cidImageHtml(url: string, cid: string): string {
  return `<img src="${escapeAttr(url)}" data-tik-cid="${escapeAttr(cid)}" style="max-width:100%">`;
}

/** Blob URL for a file, or "" where `URL.createObjectURL` does not exist (jsdom). */
export function objectUrl(file: File): string {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
}

export function revokeObjectUrl(url: string): void {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

/** Focus the editor and collapse the caret at its very start (reply: above the signature/quote). */
export function placeCaretAtStart(editor: HTMLElement): void {
  editor.focus();
  const sel = window.getSelection();
  const range = document.createRange();
  range.setStart(editor, 0);
  range.collapse(true);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

export const isImageFile = (f: File): boolean => f.type.startsWith('image/');

/** Image files carried by a paste event (other clipboard content is left to the browser). */
export function pastedImages(ev: Event): File[] {
  const out: File[] = [];
  const data = (ev as ClipboardEvent).clipboardData;
  for (const item of data?.items ?? []) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
    const f = item.getAsFile();
    if (f) out.push(f);
  }
  return out;
}

/** Files carried by a drop event. */
export function droppedFiles(ev: Event): File[] {
  return [...((ev as DragEvent).dataTransfer?.files ?? [])];
}

/**
 * "Attachment mentioned but none attached" over the live editor: quotes, `.tik-sig`,
 * `[data-tik-quote]` and anything after a "wrote:" line are stripped by `mentionsAttachment`.
 */
export function mentionsAttachmentIn(editor: HTMLElement | null | undefined, attachmentCount: number): boolean {
  return mentionsAttachment(editor?.innerHTML ?? '', editorText(editor), attachmentCount);
}

/**
 * Mobile (≤900 px): keep the fixed toolbar above the virtual keyboard by translating it up by
 * `innerHeight − visualViewport.height − visualViewport.offsetTop`. Returns a cleanup function;
 * a no-op off mobile or without `visualViewport`.
 */
export function keyboardLift(toolbar: () => HTMLElement | undefined): () => void {
  const vv = window.visualViewport;
  if (!vv || !isMobile()) return () => undefined;
  const update = () => {
    const el = toolbar();
    if (!el) return;
    const lift = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    el.style.transform = lift ? `translateY(-${lift}px)` : '';
  };
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
  return () => {
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
  };
}
